import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const PayslipApproval = () => {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState(() => {
    // Default to previous month
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    return previousMonth;
  });

  useEffect(() => {
    fetchPayslips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, month, search]);

  const fetchPayslips = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        setLoading(false);
        return;
      }

      const payload = {
        action: 'get-all-payslips',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit,
        month: month,
        ...(search && { search: search })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setPayslips(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
        // Clear selected payslips when data changes
        setSelectedPayslips([]);
      } else {
        toast.error(result?.message || 'Failed to fetch payslips');
      }
    } catch (error) {
      console.error('Error fetching payslips:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNumber - 1] || '';
  };

  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '0.00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0.00';
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleMonthChange = (e) => {
    const monthValue = parseInt(e.target.value, 10);
    if (!isNaN(monthValue) && monthValue >= 1 && monthValue <= 12) {
      setMonth(monthValue);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleCheckboxChange = (payslipId) => {
    setSelectedPayslips(prev => {
      if (prev.includes(payslipId)) {
        return prev.filter(id => id !== payslipId);
      } else {
        return [...prev, payslipId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = payslips.map(payslip => payslip._id || payslip.id).filter(Boolean);
      setSelectedPayslips(allIds);
    } else {
      setSelectedPayslips([]);
    }
  };

  const handleView = (payslipId) => {
    navigate(`/payslip-view/${payslipId}`);
  };

  const handleEdit = (payslipId) => {
    navigate(`/payslip-edit/${payslipId}`);
  };

  const handleApprove = async () => {
    if (selectedPayslips.length === 0) {
      toast.error('Please select at least one payslip to approve');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'approve-payslips',
        payslipIds: selectedPayslips
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const approvedCount = result.approvedCount ?? selectedPayslips.length;
        const totalRequested = result.totalRequested ?? selectedPayslips.length;
        const successMsg = result?.message || `${approvedCount} of ${totalRequested} payslip(s) approved successfully`;
        toast.success(successMsg);
        setSelectedPayslips([]);
        fetchPayslips();
      } else {
        toast.error(result?.message || 'Failed to approve payslips');
      }
    } catch (error) {
      console.error('Error approving payslips:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected = payslips.length > 0 && selectedPayslips.length === payslips.length;

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Payslip Approval
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Review and approve payslips for employees. Select payslips and approve them in bulk.
              </p>
            </div>
          </div>

          {/* Payslip Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px' }}>
                    <input
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search by employee name..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                    <label htmlFor="month" className="form-label" style={{ marginBottom: '5px', fontSize: '12px', color: '#6b7280', display: 'block' }}>
                      Month
                    </label>
                    <div className="date-input-wrapper" style={{ position: 'relative' }}>
                      <select
                        id="month"
                        className="form-input"
                        value={month}
                        onChange={handleMonthChange}
                        style={{
                          padding: '8px 35px 8px 12px',
                          fontSize: '14px',
                          height: '40px',
                          width: '200px',
                          appearance: 'none',
                          backgroundImage: 'none'
                        }}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>
                            {getMonthName(m)}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-calendar-alt date-icon" style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        pointerEvents: 'none',
                        fontSize: '14px'
                      }}></i>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px' }}>
                              <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                                style={{ cursor: 'pointer' }}
                              />
                            </th>
                            <th className="employee-sortable">S.No</th>
                            <th className="employee-sortable">Employee Name</th>
                            <th className="employee-sortable">Employee ID</th>
                            <th className="employee-sortable">Month</th>
                            <th className="employee-sortable">Year</th>
                            <th className="employee-sortable">Net Pay</th>
                            <th className="employee-sortable">Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payslips.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="employee-empty">
                                No payslips found
                              </td>
                            </tr>
                          ) : (
                            payslips.map((payslip, index) => {
                              const payslipId = payslip._id || payslip.id;
                              const isSelected = selectedPayslips.includes(payslipId);
                              return (
                                <tr key={payslipId}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleCheckboxChange(payslipId)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </td>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{payslip.employeeName || payslip.name || '-'}</td>
                                  <td>{payslip.employeeId || payslip.employee_id || '-'}</td>
                                  <td>{payslip.month ? getMonthName(payslip.month) : '-'}</td>
                                  <td>{payslip.year || '-'}</td>
                                  <td>₹{formatCurrency(payslip.netPay || payslip.net_pay || 0)}</td>
                                  <td>
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: payslip.status === 'Approved' ? '#d4edda' : '#fff3cd',
                                      color: payslip.status === 'Approved' ? '#155724' : '#856404'
                                    }}>
                                      {payslip.status ? payslip.status.toUpperCase() : 'PENDING'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="employee-actions">
                                      <button
                                        className="employee-action-btn employee-action-edit"
                                        onClick={() => handleView(payslipId)}
                                        title="View"
                                        style={{ marginRight: '5px' }}
                                      >
                                        <i className="fa-solid fa-eye"></i>
                                      </button>
                                      <button
                                        className="employee-action-btn employee-action-edit"
                                        onClick={() => handleEdit(payslipId)}
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pencil"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {payslips.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap', gap: '15px' }}>
                        <div>
                          {selectedPayslips.length > 0 && (
                            <button
                              type="button"
                              className="employee-back-btn"
                              onClick={handleApprove}
                              disabled={loading}
                              style={{ backgroundColor: '#0094a9', color: '#fff' }}
                            >
                              {loading ? 'Approving...' : `Approve (${selectedPayslips.length})`}
                            </button>
                          )}
                        </div>
                        {pagination.totalPages > 0 && (
                          <div className="employee-pagination">
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(1)}
                              disabled={pagination.currentPage === 1}
                            >
                              «
                            </button>
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                            >
                              ‹
                            </button>
                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            ))}
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              ›
                            </button>
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.totalPages)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              »
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PayslipApproval;
